import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { Popover } from "../../common/popover/Popover";
import { Item } from "../../styles/common";
import {
  BanTarget,
  PROPAGATION_NOTICE,
  REASON_HINT,
  describeContact,
  describeUnbanEffect,
} from "./userBan";

interface PropsInterface {
  open: boolean;
  /** `null` while closed. `isBanned` decides which of the two forms is shown. */
  target: BanTarget | null;
  saving: boolean;
  onCancel: () => void;
  /** `reason` is only ever passed when banning. */
  onConfirm: (reason?: string) => void;
}

/**
 * Confirms a ban or an un-ban, and collects the reason.
 *
 * One dialog for both directions and both entry points (the Users list and the
 * ban screen) so the consequences are described in one place. They are worth
 * describing: a ban refuses the member on *every* operation and reserves their
 * phone number and email, which is more than "delete" does and not what an admin
 * necessarily expects from a moderation action.
 *
 * Banning is confirmed rather than applied on the click because it signs somebody
 * out of the app with no notice. Un-banning is confirmed too, but only to say
 * what it restores — and to warn when it will restore nothing at all.
 */
export const BanMemberDialog: React.FC<PropsInterface> = ({
  open,
  target,
  saving,
  onCancel,
  onConfirm,
}: PropsInterface): JSX.Element => {
  const [reason, setReason] = useState("");

  // reactjs-popup keeps the children mounted, so the field has to be cleared
  // explicitly — otherwise one member's reason is prefilled onto the next.
  useEffect(() => {
    setReason("");
  }, [target?.userId, open]);

  const unbanning = Boolean(target?.isBanned);

  const unbanEffect = target
    ? describeUnbanEffect(target)
    : { warns: false, text: "" };

  return (
    <Popover open={open} handleClose={onCancel}>
      {/*
       * `Item`, not `Box`: reactjs-popup's stylesheet is never imported, so the
       * popup wrapper is transparent and the content must bring its own opaque
       * surface.
       */}
      <Item sx={{ p: 3, maxWidth: 560, boxShadow: 6 }}>
        {target && (
          <>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {unbanning
                ? `Un-ban ${target.username}?`
                : `Ban ${target.username}?`}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              id {target.userId} · {describeContact(target)}
            </Typography>

            {unbanning ? (
              /*
               * Three outcomes, one of which is "we cannot tell from here" — the
               * Users list has no anonymization field, and guessing would promise
               * a restore that may not happen. See `describeUnbanEffect`.
               */
              unbanEffect.warns ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">{unbanEffect.text}</Typography>
                </Alert>
              ) : (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {unbanEffect.text}
                </Typography>
              )
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  They are refused on <strong>every</strong> operation from their
                  next request, signed out with no notice, cannot sign in, and
                  drop out of leaderboards, member counts and broadcast
                  audiences. {PROPAGATION_NOTICE}
                </Typography>

                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Their phone number and email <strong>stay reserved</strong>, so
                  those details cannot be registered again — unlike a deletion,
                  which releases them. Asking for a verification code silently
                  does nothing: no message is sent and no error is shown, so they
                  will report a code that never arrived.
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    A ban does not remove anything they wrote — reviews,
                    recommendations and chat messages stay up, and moderating
                    them is a separate job. Nor does it stop a targeted push
                    notification. And nothing prevents them registering again
                    with a <strong>different</strong> number, subject only to the
                    signup rate limits.
                  </Typography>
                </Alert>

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Reason (optional)"
                  helperText={REASON_HINT}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  sx={{ mb: 2 }}
                />
              </>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color={unbanning ? "primary" : "error"}
                disabled={saving}
                onClick={() => onConfirm(unbanning ? undefined : reason)}
              >
                {saving
                  ? "Saving…"
                  : unbanning
                  ? `Un-ban ${target.username}`
                  : `Ban ${target.username}`}
              </Button>
            </Stack>
          </>
        )}
      </Item>
    </Popover>
  );
};
